/**
 * The server reads the Initiative list screen depends on.
 *
 * Three independent reads, each its own query so one failing does not blank the
 * others:
 *
 * - **The page itself** ({@link useInitiativeList}), `GET /api/initiatives`. Keyed
 *   by the full view state, so paging and filtering are ordinary cache entries and
 *   stepping back to a visited page is instant.
 * - **Platform labels** ({@link usePlatformOptions}), `GET
 *   /api/master-data/platform/selectable`. The list returns Platform *ids*; this
 *   read is what turns them into the display names the Platform column and the
 *   Platform filter show. Chosen over the administration endpoint because that
 *   one requires `masterdata:admin`, which a planner does not hold, while this
 *   one requires `initiative:read` - the same capability the list itself
 *   requires, so any user who can open this screen can also resolve its labels.
 * - **Sales_Cycle labels** ({@link useSalesCycleLabels}), `GET /api/sales-cycles`.
 *
 * Both label reads are cached well beyond a single interaction: master data and
 * cycle definitions change on an administrator's timescale, not a planner's, and
 * the labels are re-read on every page step.
 */
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import {
  initiativeListResponseSchema,
  salesCycleListResponseSchema,
  type InitiativeListResponse,
} from '@complan/contracts';
import { apiFetch } from '@/shared/api/client';
import {
  fetchSelectableMasterData,
  type SelectableRecord,
} from '@/shared/api/selectable-master-data';
import { queryKeys } from '@/shared/query/query-keys';
import {
  toInitiativeListRequestQuery,
  type InitiativeListState,
} from '@/shared/url/initiative-list-search-params';

/** Reference data changes on an administrator's timescale, not a planner's. */
const REFERENCE_STALE_TIME_MS = 5 * 60 * 1000;

/** A resolved label for an id-valued column or filter option. */
export interface LabelOption {
  readonly id: string;
  readonly label: string;
  /** True for a record retained only because an Initiative still references it. */
  readonly isInactive: boolean;
}

/** Fetches one page of the list for the given view state. */
export async function fetchInitiativeList(
  state: InitiativeListState,
  planningYear: number,
  carried?: URLSearchParams,
): Promise<InitiativeListResponse> {
  const params = toInitiativeListRequestQuery(state, planningYear, carried);
  const body = await apiFetch<unknown>(`/api/initiatives?${params.toString()}`);
  return initiativeListResponseSchema.parse(body);
}

/** React Query hook for one page of the list. */
export function useInitiativeList(
  state: InitiativeListState,
  planningYear: number,
  /** The screen's search params, carrying dimensions it renders no control for (R6.3). */
  carried?: URLSearchParams,
): UseQueryResult<InitiativeListResponse, unknown> {
  // Normalised into the key, so a carried dimension changing is a different query
  // rather than a stale hit. Sorted, so two orderings of one selection share an entry.
  const carriedKey = carried === undefined ? '' : [...carried.entries()].sort().join('&');
  return useQuery({
    queryKey: queryKeys.initiativeList(state, planningYear, carriedKey),
    queryFn: () => fetchInitiativeList(state, planningYear, carried),
    // A page the user steps back to should render immediately rather than
    // flashing a loading state; the list is not a real-time surface.
    staleTime: 30 * 1000,
  });
}

/**
 * The Platform records this user may see, as labels.
 *
 * `label` is the Platform's `name` (R10.5's display value), never its `code` -
 * the lowercase internal identifier master data stores for export
 * round-tripping and uniqueness. Both the Platform column tag and the Platform
 * filter option show this same name, so an Initiative reads the same way on
 * this screen and on the Calendar_View's legend and bars.
 */
export function usePlatformOptions(): UseQueryResult<readonly PlatformOption[], unknown> {
  return useQuery({
    queryKey: queryKeys.selectableMasterData('platform'),
    // The *shared* fetch, so this key holds one shape no matter which screen
    // populated it. This hook used to run its own `queryFn` returning
    // `{ id, label, isInactive, colorHex }` under the same key that
    // `shared/api/selectable-master-data.ts` fills with `SelectableRecord`s -
    // see that module's note on why that silently broke this column depending
    // on which screen the user opened first.
    queryFn: () => fetchSelectableMasterData('platform'),
    staleTime: REFERENCE_STALE_TIME_MS,
    // Projected per observer. `select` runs on the way *out* of the cache, so the
    // cached value stays the canonical `SelectableRecord[]` every other consumer
    // of this key expects.
    select: toPlatformOptions,
  });
}

/**
 * `SelectableRecord`s as Platform options.
 *
 * `label` is the Platform's `name` and never its `code`; `colorHex` is R16.1's
 * configured colour, threaded through so the Platform column tag renders in the
 * same colour the Calendar_View uses for that Platform's bars and legend. `null`
 * for a Platform with no colour set, and the tag falls back to the neutral
 * outline style.
 */
function toPlatformOptions(records: readonly SelectableRecord[]): readonly PlatformOption[] {
  return records.map((record) => ({
    id: record.id,
    label: record.name,
    isInactive: !record.isActive,
    colorHex: record.colour,
  }));
}

/**
 * A Platform option, resolving a Platform id to its display name and configured
 * colour (R16.1). `colorHex` is a `#rrggbb` string or `null` when unset.
 */
export interface PlatformOption extends LabelOption {
  readonly colorHex: string | null;
}

/**
 * Every Sales_Cycle definition, across planning years, as labels.
 *
 * Deliberately unscoped by year, unlike the calendar's own cycle read: this list
 * is not year-scoped, so a page can hold Initiatives of several years at once and
 * a single year's definitions would leave some rows' cycle ids unresolved.
 * `includeInactive` is set for the same reason - an Initiative keeps its
 * assignment after a cycle is deactivated, and an unresolvable id would render as
 * a raw UUID.
 */
export function useSalesCycleLabels(): UseQueryResult<readonly LabelOption[], unknown> {
  return useQuery({
    queryKey: queryKeys.salesCycleLabels(),
    queryFn: async (): Promise<readonly LabelOption[]> => {
      const body = await apiFetch<unknown>('/api/sales-cycles?includeInactive=true');
      const parsed = salesCycleListResponseSchema.parse(body);
      return parsed.items.map((cycle) => ({
        id: cycle.id,
        label: cycle.name,
        isInactive: !cycle.isActive,
      }));
    },
    staleTime: REFERENCE_STALE_TIME_MS,
  });
}

/**
 * An id-to-label lookup, returning `undefined` for an id it cannot resolve.
 *
 * This used to fall back to the raw id "so a cell is never blank". That trades a
 * blank cell for a worse outcome: a 36-character UUID rendered in the position a
 * display name belongs reads as corrupt data, not as a missing label, and it is
 * the same on screen whether the reference read is still in flight or failed
 * outright. Returning `undefined` hands that decision to the cell, which renders
 * an explicit unresolved marker and keeps the identifier in the tooltip where it
 * is useful for support without pretending to be the value.
 */
export function labelLookup(
  options: readonly LabelOption[] | undefined,
): (id: string) => string | undefined {
  const byId = new Map((options ?? []).map((option) => [option.id, option.label]));
  return (id) => byId.get(id);
}
